/**
 * Translation Service for Lobby2.0
 * Handles fetching and loading translation files
 */

import i18n from '../../../../i18n';

export interface ITranslation {
  [key: string]: string | ITranslation;
}

class TranslationService {
  private loadedLanguages = new Set<string>();
  
  /**
   * Fetches translation data for a specific language
   */
  public async fetchTranslations(languageCode: string, assetsHost: string, assetsHash?: string): Promise<ITranslation | null> {
    // Handle localhost development vs production asset paths (same as FontLoader)
    const isLocalhost = assetsHost.includes('localhost');
    const baseUrl = isLocalhost 
      ? `${assetsHost}/assets/portal/translations/${languageCode.toLowerCase()}.json`
      : `${assetsHost}${assetsHash ? '/' + assetsHash : ''}/assets/portal/translations/${languageCode.toLowerCase()}.json`;
    const url = assetsHash && !isLocalhost ? `${baseUrl}?hash=${assetsHash}` : baseUrl;
    
    try {
      const response = await fetch(url);
      
      if (!response.ok) {
        console.warn(`Failed to fetch translations for ${languageCode}: ${response.status}`);
        return null;
      }
      
      const data = await response.json() as ITranslation;
      return data;
    } catch (error) {
      console.error(`Error fetching translations for ${languageCode}:`, error);
      return null;
    }
  }
  
  /**
   * Loads translations for a language and adds them to i18next
   */
  public async loadLanguage(languageCode: string, assetsHost: string, assetsHash?: string): Promise<void> {
    // Standardizing and normalizing language codes to ensure consistency
    const normalizedCode = i18n.services.languageUtils.formatLanguageCode(languageCode);
    
    // Skip if already loaded
    if (this.loadedLanguages.has(normalizedCode)) {
      return;
    }
    
    try {
      const translations = await this.fetchTranslations(normalizedCode, assetsHost, assetsHash);
      
      if (translations) {
        // Add translations to i18next
        i18n.addResourceBundle(normalizedCode, 'translation', translations, true, true);
        this.loadedLanguages.add(normalizedCode);
      }
    } catch (error) {
      // Translation loading failed silently
    }
  }
  
  /**
   * Changes the current language in i18next
   */
  public async changeLanguage(languageCode: string, assetsHost: string, assetsHash?: string): Promise<void> {
    // Standardizing and normalizing language codes to ensure consistency
    const targetLanguage = i18n.services.languageUtils.formatLanguageCode(languageCode);

    // Skip if assetsHost not properly configured
    if (!assetsHost || assetsHost === '') {
      return;
    }

    // Skip if assetsHost is example.com (mock mode)
    if (assetsHost.includes('example.com')) {
      return;
    }

    // For CDN environments, wait for assetsHash to be available
    if (assetsHost.includes('cdn') && (!assetsHash || assetsHash === '')) {
      return;
    }

    // Only use localhost:3001 in actual development environment
    if (process.env['NODE_ENV'] === 'development' && assetsHost === 'http://localhost:3001') {
      assetsHost = 'http://localhost:3001';
      assetsHash = '';
    }
    
    // Load the language if not already loaded
    await this.loadLanguage(targetLanguage, assetsHost, assetsHash);
    
    // Change the language in i18next
    if (i18n.language !== targetLanguage) {
      await i18n.changeLanguage(targetLanguage);
    }

    /*
      Text that is scaled to fit is measured in a layout effect, i.e. before paint. Dispatching
      straight after the await is too early: React has not rendered the new strings yet. One
      frame later it has — and a script that needs another font file is laid out with the
      fallback until that font arrives, which changes the widths a second time.
    */
    const remeasure = () => window.dispatchEvent(new CustomEvent('force-text-scale'));
    requestAnimationFrame(remeasure);
    void document.fonts?.ready.then(() => requestAnimationFrame(remeasure));
  }
  
  /**
   * Initializes the translation service with default language
   */
  public async initialize(defaultLanguage: string, assetsHost: string, assetsHash?: string): Promise<void> {
    // Standardizing and normalizing language codes to ensure consistency
    const normalizedDefault = i18n.services.languageUtils.formatLanguageCode(defaultLanguage);

    // Skip if assetsHost not properly configured
    if (!assetsHost || assetsHost === '') {
      return;
    }

    // Skip if assetsHost is example.com (mock mode)
    if (assetsHost.includes('example.com')) {
      return;
    }

    // For CDN environments, wait for assetsHash to be available
    if (assetsHost.includes('cdn') && (!assetsHash || assetsHash === '')) {
      return;
    }

    // Only use localhost:3001 in actual development environment
    if (process.env['NODE_ENV'] === 'development' && assetsHost === 'http://localhost:3001') {
      assetsHost = 'http://localhost:3001';
      assetsHash = '';
    }
    
    await this.loadLanguage(normalizedDefault, assetsHost, assetsHash);
    
    // Set the initial language if not already set
    if (!i18n.language || i18n.language === 'en') {
      await i18n.changeLanguage(normalizedDefault);
    }
  }
  
  /**
   * Clears the loaded languages cache (useful for testing)
   */
  public clearCache(): void {
    this.loadedLanguages.clear();
  }
}

export default new TranslationService();